import { autoinject, transient } from "aurelia-dependency-injection";
import { Claims } from "../../../../../../constants/authentication/claims";
import { RightShipInspectionModel, RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { ExistingBillingAccountModel } from "../../../../../../models/billing/billing.models";
import { BillingAccountService } from "../../../../../../services/billing/billing.service";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Messages } from "../../../../../../configuration/event-messages.constants";
import * as IntlTelInput from "intl-tel-input";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";

@autoinject
@transient()
export class InvoiceTransmissionDetailsTab {
    phoneCountryCode: string;
    invoiceEmails: { email: string }[] = [];
    canModifyBillingDetails: boolean;
    canEditBillingDetailsCurrencyAmountSales: boolean;
    item: RightShipInspectionModel;
    hasLegacyInvoice: boolean;
    telInput: IntlTelInput.Plugin = null;
    private _eventAggregatorSubscriptions: Subscription[] = [];
    public companyHasOverdueInvoice: boolean;
    public existingBillingCompanies: ExistingBillingAccountModel[] = [];
    public enterNewBillingDetails: boolean;
    public selectedExistingBillingCompany: ExistingBillingAccountModel;
    public useExistingBillingDetails: boolean;
    public currencyConverionRates: any[] = [];

    constructor(userClaimsService: UserClaimsService, private billingAccountService: BillingAccountService, private eventAggregator: EventAggregator, private lookupService: LookupClientService) {
        this.canModifyBillingDetails = userClaimsService.hasPermission(Claims.CanModifyBillingDetails);
        this.canEditBillingDetailsCurrencyAmountSales = userClaimsService.hasPermission(Claims.CanEditBillingDetailsCurrencyAmountSales);
    }

    async activate(item: RightShipInspectionModel) {
        this.item = item;
        this.initModels();
    }

    async attached() {
        this.initIntlTel();
        await this.getExistingBillingCompanies();

        const subscription = this.eventAggregator.subscribe(Messages.InspectedVesselChanged, async () => {
            await this.getExistingBillingCompanies();
        });
        this._eventAggregatorSubscriptions.push(subscription);

        this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.CurrencyConversionRates)).then((response) => {
            this.currencyConverionRates = response.activeValues.map(x => { return { id: x.id, name: x.name, rate: x.additionalData['rate']} });
            this.calTotalSupplierInvoiceAmount();
        });
    }

    detached() {
        this._eventAggregatorSubscriptions?.forEach(subscription => {
            subscription.dispose();
        });
        this._eventAggregatorSubscriptions = [];
    }

    initIntlTel() {
        const input = document.querySelector("#phone-number");
        this.telInput = IntlTelInput(input, {
            hiddenInput: "full",
            separateDialCode: true,
            preferredCountries: ["gb", "us", "sg", "au"],
            utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@16.0.3/build/js/utils.js"
        });
        this.telInput.setCountry('');
    }

    private async getExistingBillingCompanies() {
        if (this.item && (this.item.vesselId || this.item.rightShipInspectionId > 0)) {
            this.existingBillingCompanies = await this.billingAccountService.getExistingBillingAccountForInspection(this.item.rightShipInspectionId, this.item.vesselId);
            const  existingCompany = this.existingBillingCompanies.find(x => this.item.invoices && this.item.invoices[0] 
                                                                        && x.billingAccountId == this.item.invoices[0].billingAccountId);
            if (existingCompany) {//if the selected company is still in the list
                this.selectExistingCompany(existingCompany);
            } else if (!existingCompany && this.item.rightShipInspectionId > 0){//if editing and the selected company is not in the list
                this.enterNewBillingDetails = true;
            } else if (this.existingBillingCompanies.length > 0)            {
                this.selectExistingCompany(this.existingBillingCompanies[0]);
            }
            else {
                this.enterNewBillingDetails = true;
            }
        }
    }

    //Select existing company for "Use existing billing details" option
    public selectExistingCompany(company: ExistingBillingAccountModel) {
        this.useExistingBillingDetails = true;
        this.enterNewBillingDetails = false;
        this.selectedExistingBillingCompany = company;
        this.setExistingCompanyFields(company);
    }

    private setExistingCompanyFields(company: ExistingBillingAccountModel) {
        if (this.item && this.item.invoices && this.item.invoices[0]) {
            this.item.invoices[0].companyName = company.companyName;
            this.item.invoices[0].contactName = company.contactName;
            this.item.invoices[0].address = company.address;
            this.item.invoices[0].city = company.city;
            this.item.invoices[0].state = company.stateProvince;
            this.item.invoices[0].countryId = company.countryId;
            this.item.invoices[0].postCode = company.postalCode;
            this.item.invoices[0].phoneNumber = company.phoneNumber;
            this.item.invoices[0].purchaseOrderNo = company.purchaseOrderNumber;
            this.item.invoices[0].email = company.emailAddress;
            this.item.invoices[0].billingAccountId = company.billingAccountId;
            this.item.invoices[0].phoneNumberWithoutCode = company.phoneNumber;
        }
        else {
            console.warn("No invoice found to set existing company fields");
        }
    }

    countryChanged(selectedCountry: any) {
        if (selectedCountry && selectedCountry.additionalData) {
            const iso2Code = selectedCountry.additionalData.isO2Code;
            const countryData = this.telInput.countries;
            const country = countryData.find(x => x.iso2?.toLowerCase() == iso2Code?.toLowerCase());
            if (iso2Code && iso2Code != "" && country) {
                this.telInput.setCountry(country.iso2);
                this.item.invoices[0].phoneNumberCountryCode = "+" + country.dialCode;
            } else {
                this.telInput.setCountry('');
                this.item.invoices[0].phoneNumberCountryCode = null;
            }
        }
        
    }

    //Select "Enter new billing details" option
    public selectEnterNewBillingDetails() {
        if (this.item && this.item.invoices && this.item.invoices[0]) {
            this.item.invoices[0].companyName = null;
            this.item.invoices[0].contactName = null;
            this.item.invoices[0].address = null;
            this.item.invoices[0].city = null;
            this.item.invoices[0].state = null;
            this.item.invoices[0].countryId = null;
            this.item.invoices[0].postCode = null;
            this.item.invoices[0].phoneNumber = null;
            this.item.invoices[0].purchaseOrderNo = null;
            this.item.invoices[0].email = null;
            this.item.invoices[0].billingAccountId = null;
            this.item.invoices[0].phoneNumberCountryCode = null;
            this.item.invoices[0].phoneNumberWithoutCode = null;
            this.item.invoices[0].billingInstructions = null;
        }
        else {
            console.warn("No invoice found to set existing company fields");
        }
        this.useExistingBillingDetails = false;
        this.enterNewBillingDetails = true;
    }

    today() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    removeInvoiceEmail(index) {
        if (!this.item.invoices[0])
            return;

        this.setInvoiceEmailsString();
        this.item.invoices[0].additionalEmailsToNotify = this.removeEmailFromList(index, this.invoiceEmails);
        this.invoiceEmails = this.getListOfEmails(this.item.invoices[0].additionalEmailsToNotify);
    }

    removeEmailFromList(index: any, emails: any) {
        let emailsAsString = null;

        for (let i = 0; i < emails.length; i++) {
            if (index != i) {
                if (i > 0)
                    emailsAsString += ";";

                emailsAsString += emails[i].email;
            }
        }

        return emailsAsString;
    }

    setInvoiceEmailsString() {
        if (!this.item || !this.item.invoices[0])
            return;

        if (this.invoiceEmails.length > 0 && this.item.invoices.length > 0) {
            this.item.invoices[0].additionalEmailsToNotify = this.invoiceEmails.map(x => x.email).join(";");
        } else {
            this.item.invoices[0].additionalEmailsToNotify = null;
        }
    }

    getListOfEmails(emails: string) {
        let listOfEmails = [];

        if (emails != null) {
            listOfEmails = emails.split(";").map(x => {
                return { email: x.trim() };
            });
        }

        return listOfEmails;
    }

    addInvoiceEmail() {
        this.setInvoiceEmailsString();

        if (this.item.invoices && this.item.invoices.length > 0) {
            if (!this.item.invoices[0].additionalEmailsToNotify && this.item.invoices[0].additionalEmailsToNotify != "")
                this.item.invoices[0].additionalEmailsToNotify = "";
            else
                this.item.invoices[0].additionalEmailsToNotify += ";";

            this.invoiceEmails = this.getListOfEmails(this.item.invoices[0].additionalEmailsToNotify);
        } else {
            this.initModels();
        }
    }

    get isCargill() {
        return this.item && this.item.rightShipInspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    initModels() {
        if (!this.isCargill) {

            if (this.item && this.item.invoices.length == 0) {
                this.item.invoices.push({
                    inspectionMarkup: null,
                    invoiceReceivedDate: null,
                    address: null,
                    billingCustomerId: null,
                    city: null,
                    companyName: null,
                    contactName: null,
                    countryId: null,
                    email: "",
                    additionalEmailsToNotify: null,
                    invoiceNumber: null,
                    invoiceStatusId: null,
                    phoneNumber: null,
                    postCode: null,
                    billingInstructions: null,
                    state: null,
                    supplierInvoiceCurrency: null,
                    supplierInspectionAmount: 0,
                    supplierTravelAndExpenseAmount: 0,
                    legacySupplierInvoiceTotal: null,
                    salesCurrency: 'USD',
                    salesAmount: null,
                    purchaseOrderNo: null,
                    billingAccountId: null,
                    totalSupplierInvoiceAmount: null,
                    currencyConversionRateId: null,
                    supplierTravelAndExpenseAmountUSD: null
                });
            }

            if (this.item && this.item.invoices[0]) {
                this.invoiceEmails = this.getListOfEmails(this.item.invoices[0].additionalEmailsToNotify);

                this.hasLegacyInvoice = !!this.item.invoices[0].legacySupplierInvoiceTotal;
            }
        }
    }

    calTotalSupplierInvoiceAmount() {
        const currencyConverionRate = this.currencyConverionRates.find(x => x.name == this.item.invoices[0].supplierInvoiceCurrency);
        if (currencyConverionRate && currencyConverionRate.rate) {
            const rate = currencyConverionRate.rate;
            this.item.invoices[0].totalSupplierInvoiceAmount = (this.item.invoices[0].supplierInspectionAmount + this.item.invoices[0].supplierTravelAndExpenseAmount) * rate;
            this.item.invoices[0].supplierTravelAndExpenseAmountUSD = this.item.invoices[0].supplierTravelAndExpenseAmount * rate
            this.item.invoices[0].currencyConversionRateId = currencyConverionRate.id;
        } else {
            this.item.invoices[0].totalSupplierInvoiceAmount = null;
            this.item.invoices[0].supplierTravelAndExpenseAmountUSD = null;
        }
    }
}
